import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import {
  BarChart3Icon,
  ClockIcon,
  DownloadIcon,
  TrendingUpIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import {
  useClassCapacityPeakTimesQuery,
  useClassCapacitySessionsQuery,
  useClassCapacitySummaryQuery,
} from "../api/queries";
import {
  ClassCapacityReportFilter,
  type ClassCapacityReportSearch,
} from "../ui/ClassCapacityReportFilter";
import { ClassCapacitySessionsTable } from "../ui/ClassCapacitySessionsTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function useReportQuery(search: ClassCapacityReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;

  const baseQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      classTypeId: search.classTypeId,
      levelId: search.levelId,
      roomId: search.roomId,
      locationKind: search.locationKind,
      engagementKind: search.engagementKind,
    }),
    [
      dateFrom,
      dateTo,
      search.classTypeId,
      search.levelId,
      search.roomId,
      search.locationKind,
      search.engagementKind,
    ],
  );

  const sessionsQuery = useMemo(
    () => ({
      ...baseQuery,
      sortBy: "date" as const,
      sortDir: "asc" as const,
      limit: 100,
      offset: 0,
    }),
    [baseQuery],
  );

  return { baseQuery, sessionsQuery };
}

const peakTimesChartConfig: ChartConfig = {
  hourSlot: { label: "ເວລາ" },
  avgUtilization: { label: "ອັດຕາການໃຊ້ຄວາມຈຳ %", color: "var(--color-chart-1)" },
};

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const STATUS_LABEL: Record<"full" | "empty" | "normal", string> = {
  full: "ເຕັມ",
  empty: "ວ່າງ",
  normal: "ປານກາງ",
};

export function ClassCapacityUtilizationReportPage() {
  const search = useSearch({
    from: "/app/reports/class-capacity",
  }) as ClassCapacityReportSearch;

  const { baseQuery, sessionsQuery } = useReportQuery(search);

  const { data: summary, isLoading: summaryLoading } =
    useClassCapacitySummaryQuery(baseQuery);
  const { data: sessionsData, isLoading: sessionsLoading } =
    useClassCapacitySessionsQuery(sessionsQuery);
  const { data: peakTimes = [] } = useClassCapacityPeakTimesQuery(baseQuery);

  const peakChartData = useMemo(
    () =>
      peakTimes.map((r) => ({
        hourSlot: r.hourSlot,
        avgUtilization: r.avgUtilization,
      })),
    [peakTimes],
  );

  const sessions = sessionsData?.data ?? [];

  const exportCsv = useCallback(() => {
    const header =
      "ວັນທີ,ເວລາ,ປະເພດຄລາດສ໌,ລະດັບ,location_kind,off_site_label,engagement_kind,room_name,ສະຖານທີ່ສະແດງ,ເຂົ້າຮຽນ,ຄວາມຈຳ,ອັດຕາ%,ສະຖານະ";
    const rows = sessions.map((r) => {
      const placeDisplay =
        r.locationKind === "studio" ? r.roomName : r.offSiteLabel?.trim() || "";
      return `${escapeCsvCell(r.date)},${escapeCsvCell(r.startTime)},${escapeCsvCell(r.classTypeName)},${escapeCsvCell(r.levelName)},${escapeCsvCell(r.locationKind)},${escapeCsvCell(r.offSiteLabel ?? "")},${escapeCsvCell(r.engagementKind)},${escapeCsvCell(r.roomName)},${escapeCsvCell(placeDisplay)},${escapeCsvCell(r.attendance)},${escapeCsvCell(r.capacity)},${escapeCsvCell(r.utilizationPercent.toFixed(1))},${escapeCsvCell(STATUS_LABEL[r.status])}`;
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `class-capacity-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sessions]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານການໃຊ້ຄວາມຈຸຄລາດສ໌" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານການໃຊ້ຄວາມຈຳຄລາດສ໌</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງອັດຕາການໃຊ້ຄວາມຈຳ ຄລາດສ໌ທີ່ເຕັມ/ວ່າງ ແລະເວລາເຄັບ — ລວມຄລາດສ໌ນອກສະຖານທີ່
              ແລະ Partner (ບໍ່ຕັດແພັກເກດ) ເພື່ອແຍກກັບມູມການຂາຍ/consumption
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={sessions.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <ClassCapacityReportFilter />
        </div>

        <ReportSummaryStatGrid columns={5}>
          <ReportSummaryStatCard
            title="ອັດຕາການໃຊ້ຄວາມຈຳໂດຍສະເລ່ຍ"
            icon={BarChart3Icon}
            tone="attendance"
            isLoading={summaryLoading}
            value={`${(summary?.avgUtilizationPercent ?? 0).toFixed(1)}%`}
          />
          <ReportSummaryStatCard
            title="ຄລາດສ໌ທີ່ເຕັມ"
            icon={TrendingUpIcon}
            tone="revenue"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.fullClassesCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ຄລາດສ໌ທີ່ວ່າງ"
            icon={BarChart3Icon}
            tone="packages"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.emptyClassesCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ເວລາເຄັບ"
            icon={ClockIcon}
            tone="students"
            isLoading={summaryLoading}
            value={summary?.peakTimeSlot ?? "—"}
          />
          <ReportSummaryStatCard
            title="ຈຳນວນຄລາດສ໌"
            icon={BarChart3Icon}
            tone="refunds"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.totalSessionsCount ?? 0)}
          />
        </ReportSummaryStatGrid>

        {peakChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>ອັດຕາການໃຊ້ຄວາມຈຳຕາມເວລາ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ສະເລ່ຍອັດຕາການໃຊ້ຄວາມຈຳໃນແຕ່ລະຊົ່ວໂມງ
              </p>
            </CardHeader>
            <CardContent>
              <BarChart
                data={peakChartData}
                config={peakTimesChartConfig}
                xDataKey="hourSlot"
                valueDataKey="avgUtilization"
                className="mx-auto aspect-video max-h-[300px]"
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>ລາຍການຄລາດສ໌</CardTitle>
            <p className="text-muted-foreground text-sm">
              ວັນທີ ເວລາ ປະເພດ ລະດັບ ສະຖານທີ່ ຮູບແບບ ເຂົ້າຮຽນ/ຄວາມຈຳ ອັດຕາ ສະຖານະ
            </p>
          </CardHeader>
          <CardContent>
            <ClassCapacitySessionsTable
              isLoading={sessionsLoading}
              data={sessions}
            />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
